import { useRef, useState } from 'react';
import { ActivityIndicator, Animated, Pressable, StyleSheet, Text, type GestureResponderEvent, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { colors, control, fontFamily, radii, spacing } from '~/ui/tokens';
import { useReducedMotion } from '~/hooks/useReducedMotion';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type ButtonSize = 'default' | 'sm';

interface ButtonProps {
  label: string;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type Ripple = { id: number; x: number; y: number; anim: Animated.Value };

export function Button({
  label,
  onPress,
  variant = 'primary',
  size = 'default',
  disabled = false,
  loading = false,
  style,
  labelStyle,
}: ButtonProps) {
  const [hovered, setHovered] = useState(false);
  const isInactive = disabled || loading;
  const reduceMotion = useReducedMotion();
  const scale = useRef(new Animated.Value(1)).current;
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const nextRippleId = useRef(0);

  function handlePressIn(event: GestureResponderEvent) {
    if (isInactive) return;
    if (!reduceMotion) {
      Animated.spring(scale, { toValue: 0.97, friction: 9, tension: 180, useNativeDriver: true }).start();

      const id = nextRippleId.current++;
      const anim = new Animated.Value(0);
      const { locationX, locationY } = event.nativeEvent;
      setRipples((current) => [...current, { id, x: locationX, y: locationY, anim }]);
      Animated.timing(anim, { toValue: 1, duration: 480, useNativeDriver: true }).start(() => {
        setRipples((current) => current.filter((ripple) => ripple.id !== id));
      });
    }
  }

  function handlePressOut() {
    if (isInactive || reduceMotion) return;
    Animated.spring(scale, { toValue: 1, friction: 9, tension: 180, useNativeDriver: true }).start();
  }

  const rippleSize = Math.max(box.width, box.height) * 2.2;
  const rippleColor = variantStyles[variant].ripple;

  return (
    <AnimatedPressable
      accessibilityRole="button"
      accessibilityState={{ disabled: isInactive }}
      onPress={isInactive ? undefined : onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onLayout={(event) => setBox({ width: event.nativeEvent.layout.width, height: event.nativeEvent.layout.height })}
      style={[
        styles.base,
        variantStyles[variant].container,
        size === 'sm' && styles.sm,
        hovered && !isInactive && variantStyles[variant].hoverContainer,
        isInactive && styles.disabled,
        { transform: [{ scale }] },
        style,
      ]}
    >
      {ripples.map((ripple) => (
        <Animated.View
          key={ripple.id}
          pointerEvents="none"
          style={[
            styles.ripple,
            {
              backgroundColor: rippleColor,
              width: rippleSize,
              height: rippleSize,
              borderRadius: rippleSize / 2,
              left: ripple.x - rippleSize / 2,
              top: ripple.y - rippleSize / 2,
              opacity: ripple.anim.interpolate({ inputRange: [0, 0.6, 1], outputRange: [1, 0.5, 0] }),
              transform: [{ scale: ripple.anim.interpolate({ inputRange: [0, 1], outputRange: [0, 1] }) }],
            },
          ]}
        />
      ))}
      {loading ? (
        <ActivityIndicator color={variantStyles[variant].text.color as string} size="small" />
      ) : (
        <Text style={[styles.label, size === 'sm' && styles.labelSm, variantStyles[variant].text, labelStyle]}>
          {label}
        </Text>
      )}
    </AnimatedPressable>
  );
}

const styles = StyleSheet.create({
  base: {
    borderRadius: radii.md,
    paddingVertical: spacing(1.5),
    paddingHorizontal: spacing(3),
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  sm: {
    paddingVertical: spacing(1.25),
    paddingHorizontal: spacing(2.5),
  },
  label: {
    fontFamily: fontFamily.semibold,
    ...control.primaryCta,
  },
  labelSm: {
    ...control.cardCta,
  },
  disabled: {
    opacity: 0.4,
  },
  ripple: {
    position: 'absolute',
  },
});

const variantStyles: Record<
  ButtonVariant,
  { container: ViewStyle; hoverContainer: ViewStyle; text: { color: string }; ripple: string }
> = {
  primary: {
    container: { backgroundColor: colors.brand },
    hoverContainer: { backgroundColor: colors.brandDeep },
    text: { color: colors.white },
    ripple: 'rgba(255,255,255,0.35)',
  },
  secondary: {
    container: { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line },
    hoverContainer: { borderColor: colors.brand },
    text: { color: colors.ink },
    ripple: 'rgba(22,58,186,0.12)',
  },
  tertiary: {
    container: { backgroundColor: 'transparent' },
    hoverContainer: { backgroundColor: colors.skyTint },
    text: { color: colors.brand },
    ripple: 'rgba(22,58,186,0.12)',
  },
};
